import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import AdminLayout from '../components/Layout/AdminLayout';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import { notificationsAPI } from '../services/api';
import { Send, Bell, AlertTriangle, CheckCircle } from 'lucide-react';

const Notifications = () => {
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [formData, setFormData] = useState(null);
  
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm();

  const watchedTitle = watch('title', '');
  const watchedMessage = watch('message', '');

  const onSubmit = (data) => {
    setFormData(data);
    setConfirmModal(true);
  };

  const sendNotification = async () => {
    if (!formData) return;

    setLoading(true);
    try {
      await notificationsAPI.sendBroadcast(formData.title, formData.message);

      toast.success('تم إرسال الإشعار لجميع المستخدمين بنجاح');
      reset();
      setConfirmModal(false);
      setFormData(null);
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('فشل في إرسال الإشعار');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-accent-gold/20 rounded-full flex items-center justify-center mb-4">
            <Bell className="h-8 w-8 text-accent-gold" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">إرسال إشعار جماعي</h1>
          <p className="text-text-secondary mt-2">إرسال إشعارات لجميع مستخدمي التطبيق</p>
        </div>

        {/* Notification Form */}
        <div className="bg-card rounded-xl border border-divider p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                عنوان الإشعار *
              </label>
              <input
                {...register('title', { 
                  required: 'عنوان الإشعار مطلوب',
                  maxLength: { value: 100, message: 'العنوان لا يجب أن يتجاوز 100 حرف' }
                })}
                className="w-full px-4 py-3 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold"
                placeholder="مثال: إعلان مهم - فعالية خاصة"
                maxLength={100}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
              )}
              <p className="text-xs text-text-muted mt-1">
                {watchedTitle.length}/100 حرف
              </p>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                نص الإشعار *
              </label>
              <textarea
                {...register('message', { 
                  required: 'نص الإشعار مطلوب',
                  maxLength: { value: 500, message: 'النص لا يجب أن يتجاوز 500 حرف' }
                })}
                rows="6"
                className="w-full px-4 py-3 bg-primary-light border border-divider rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-gold resize-none"
                placeholder="اكتب هنا نص الإشعار الذي سيتم إرساله لجميع المستخدمين..."
                maxLength={500}
              />
              {errors.message && (
                <p className="text-red-500 text-sm mt-1">{errors.message.message}</p>
              )}
              <p className="text-xs text-text-muted mt-1">
                {watchedMessage.length}/500 حرف
              </p>
            </div>

            {/* Preview */}
            {(watchedTitle || watchedMessage) && (
              <div className="bg-primary-light rounded-lg p-4">
                <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center">
                  <Bell className="w-4 h-4 ml-2" />
                  معاينة الإشعار
                </h4>
                <div className="bg-card rounded-lg p-4 border border-divider">
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="bg-accent-gold/20 p-2 rounded-full">
                      <Bell className="w-4 h-4 text-accent-gold" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-text-primary">
                        {watchedTitle || 'عنوان الإشعار'}
                      </h5>
                      <p className="text-sm text-text-secondary mt-1">
                        {watchedMessage || 'نص الإشعار'}
                      </p>
                      <p className="text-xs text-text-muted mt-2">الآن</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 ml-3 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">تنبيه مهم</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    سيتم إرسال هذا الإشعار لجميع المستخدمين المسجلين في التطبيق. 
                    تأكد من صحة المحتوى قبل الإرسال.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center">
              <Button
                type="submit"
                size="lg"
                disabled={!watchedTitle || !watchedMessage}
                className="min-w-48"
              >
                <Send className="w-5 h-5 ml-2" />
                إرسال الإشعار
              </Button>
            </div>
          </form>
        </div>

        {/* Quick Tips */}
        <div className="bg-card rounded-xl border border-divider p-6">
          <h3 className="text-lg font-semibold text-text-primary mb-4">نصائح للإشعارات الفعالة</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3 space-x-reverse">
              <CheckCircle className="w-5 h-5 text-accent-gold mt-0.5" />
              <div>
                <h4 className="font-medium text-text-primary">عنوان واضح</h4>
                <p className="text-sm text-text-secondary">استخدم عنوان مختصر وواضح</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 space-x-reverse">
              <CheckCircle className="w-5 h-5 text-accent-gold mt-0.5" />
              <div>
                <h4 className="font-medium text-text-primary">محتوى مفيد</h4>
                <p className="text-sm text-text-secondary">تأكد من أن المحتوى مفيد للمستخدمين</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 space-x-reverse">
              <CheckCircle className="w-5 h-5 text-accent-gold mt-0.5" />
              <div>
                <h4 className="font-medium text-text-primary">توقيت مناسب</h4>
                <p className="text-sm text-text-secondary">أرسل الإشعارات في أوقات مناسبة</p>
              </div>
            </div>
            <div className="flex items-start space-x-3 space-x-reverse">
              <CheckCircle className="w-5 h-5 text-accent-gold mt-0.5" />
              <div>
                <h4 className="font-medium text-text-primary">تجنب الإكثار</h4>
                <p className="text-sm text-text-secondary">لا ترسل إشعارات كثيرة لتجنب الإزعاج</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal}
        onClose={() => {
          setConfirmModal(false);
          setFormData(null);
        }}
        title="تأكيد الإرسال"
        size="md"
      >
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 ml-2" />
              <p className="text-yellow-800 font-medium">هل أنت متأكد من إرسال هذا الإشعار؟</p>
            </div>
            <p className="text-yellow-700 text-sm mt-2">
              سيتم إرسال الإشعار فوراً لجميع المستخدمين ولا يمكن التراجع عن هذا الإجراء.
            </p>
          </div>

          {formData && (
            <div className="bg-primary-light rounded-lg p-4">
              <h4 className="font-medium text-text-primary mb-2">معاينة الإشعار:</h4>
              <div className="bg-card rounded p-3 border border-divider">
                <h5 className="font-medium text-text-primary">{formData.title}</h5>
                <p className="text-text-secondary text-sm mt-1">{formData.message}</p>
              </div>
            </div>
          )}

          <div className="flex space-x-3 space-x-reverse justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmModal(false);
                setFormData(null);
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={sendNotification}
              loading={loading}
            >
              <Send className="w-4 h-4 ml-1" />
              تأكيد الإرسال
            </Button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default Notifications;